import type { Candle } from "../../types";

// 2B1A — "2 Bars Before + 1 Bar After" swing identification (strict comparisons, no fractals, no indicators).
//   ohlc mode (5s+): High[i] > High[i-1] && High[i] > High[i-2] && High[i] > High[i+1]; lows mirror it. Known once i+1 CLOSED.
//   tick mode (1s synthetic): the candle open is inherited from the previous tick (rendering artifact), so the rule runs on the
//   canonical source tick price (= candle close, one tick per candle). Known as soon as tick i+1 exists.

export type TwoBOneAType = "2B1A_SWING_HIGH" | "2B1A_SWING_LOW";
export type TwoBOneAMode = "ohlc" | "tick";

export interface TwoBOneAConfig {
  forming: boolean; // ohlc: treat the last candle as still forming (live) → it cannot confirm
  mode: TwoBOneAMode;
}
export const DEFAULT_2B1A_CONFIG: TwoBOneAConfig = { forming: true, mode: "ohlc" };

export interface TwoBOneAEvent {
  type: TwoBOneAType;
  index: number; // candidate candle / tick index
  candleTime: number; // candidate time (plot anchor)
  price: number; // candidate high/low (ohlc) or source tick price (tick)
  confirmationTime: number; // time of the +1 candle/tick that confirmed it
  mode: TwoBOneAMode;
  symbol?: string;
  timeframe?: string;
}

export interface TwoBOneAResult { highs: TwoBOneAEvent[]; lows: TwoBOneAEvent[]; lastEvaluated: number; mode: TwoBOneAMode }

const hi = (c: Candle, m: TwoBOneAMode) => (m === "tick" ? c.close : c.high);
const lo = (c: Candle, m: TwoBOneAMode) => (m === "tick" ? c.close : c.low);

export const isTwoBOneAHigh = (c: Candle[], i: number, m: TwoBOneAMode = "ohlc"): boolean =>
  i >= 2 && i + 1 < c.length && hi(c[i], m) > hi(c[i - 1], m) && hi(c[i], m) > hi(c[i - 2], m) && hi(c[i], m) > hi(c[i + 1], m);

export const isTwoBOneALow = (c: Candle[], i: number, m: TwoBOneAMode = "ohlc"): boolean =>
  i >= 2 && i + 1 < c.length && lo(c[i], m) < lo(c[i - 1], m) && lo(c[i], m) < lo(c[i - 2], m) && lo(c[i], m) < lo(c[i + 1], m);

const evaluate = (c: Candle[], i: number, m: TwoBOneAMode, out: TwoBOneAResult) => {
  if (isTwoBOneAHigh(c, i, m)) out.highs.push({ type: "2B1A_SWING_HIGH", index: i, candleTime: c[i].time, price: hi(c[i], m), confirmationTime: c[i + 1].time, mode: m });
  if (isTwoBOneALow(c, i, m)) out.lows.push({ type: "2B1A_SWING_LOW", index: i, candleTime: c[i].time, price: lo(c[i], m), confirmationTime: c[i + 1].time, mode: m });
};

// Last candidate index whose +1 confirmation bar is KNOWN (tick: exists · ohlc: closed).
const lastConfirmable = (n: number, cfg: TwoBOneAConfig) => (cfg.mode === "ohlc" && cfg.forming ? n - 3 : n - 2);

export const TwoBOneAEngine = {
  /** Full historical scan — O(n), 3 comparisons per side per candidate. */
  find(candles: Candle[], cfg: TwoBOneAConfig = DEFAULT_2B1A_CONFIG): TwoBOneAResult {
    const out: TwoBOneAResult = { highs: [], lows: [], lastEvaluated: -1, mode: cfg.mode };
    const last = lastConfirmable(candles.length, cfg);
    for (let i = 2; i <= last; i++) evaluate(candles, i, cfg.mode, out);
    out.lastEvaluated = Math.max(-1, last);
    return out;
  },
};

// Incremental live state: re-evaluates only candidates that gained a known +1 bar since the last update.
// Falls back to a full rescan when the series is replaced (symbol/timeframe change, history reload, replay seek) or mode changes.
export class TwoBOneAState {
  private cfg: TwoBOneAConfig = DEFAULT_2B1A_CONFIG;
  private result: TwoBOneAResult = { highs: [], lows: [], lastEvaluated: -1, mode: "ohlc" };
  private anchorTime = NaN; // time of candles[lastEvaluated] — detects series replacement
  private length = 0;
  private lastInput: Candle[] | null = null;
  private lastOut: { result: TwoBOneAResult; fresh: TwoBOneAEvent[] } | null = null;

  /** Returns the current result plus the events that became known in this update. Idempotent for the same series ref. */
  update(candles: Candle[], cfg: TwoBOneAConfig = DEFAULT_2B1A_CONFIG): { result: TwoBOneAResult; fresh: TwoBOneAEvent[] } {
    const sameCfg = cfg.mode === this.cfg.mode && cfg.forming === this.cfg.forming;
    if (sameCfg && candles === this.lastInput && this.lastOut) return this.lastOut;
    const out = this.step(candles, cfg, sameCfg);
    this.cfg = cfg; this.lastInput = candles; this.lastOut = out;
    return out;
  }

  private step(candles: Candle[], cfg: TwoBOneAConfig, sameCfg: boolean): { result: TwoBOneAResult; fresh: TwoBOneAEvent[] } {
    const n = candles.length, last = lastConfirmable(n, cfg);
    const prev = this.result.lastEvaluated;
    const continuous = sameCfg && prev >= 0 && n >= this.length && candles[prev] && candles[prev].time === this.anchorTime;
    if (!continuous) { // series replaced → rescan; nothing is "fresh" (history is seeded, not announced)
      this.result = TwoBOneAEngine.find(candles, cfg);
      this.commit(candles);
      return { result: this.result, fresh: [] };
    }
    if (last <= prev) { this.length = n; return { result: this.result, fresh: [] }; }
    const inc: TwoBOneAResult = { highs: [], lows: [], lastEvaluated: last, mode: cfg.mode };
    for (let i = prev + 1; i <= last; i++) evaluate(candles, i, cfg.mode, inc);
    this.result = { highs: [...this.result.highs, ...inc.highs], lows: [...this.result.lows, ...inc.lows], lastEvaluated: last, mode: cfg.mode };
    this.commit(candles);
    return { result: this.result, fresh: [...inc.highs, ...inc.lows].sort((a, b) => a.index - b.index) };
  }

  private commit(candles: Candle[]) {
    this.length = candles.length;
    const i = this.result.lastEvaluated;
    this.anchorTime = i >= 0 && candles[i] ? candles[i].time : NaN;
  }
}
